import { RefObject } from 'react';

const TAP_SLOP = 5;
export const TAP_DELAY = 50;

interface TouchPoint {
    pageX: number;
    pageY: number;
}

interface Scroller {
    isScrolling: boolean;
}

interface SwipeMenuList {
    openIndex: number;
}

interface GestureProps {
    domRef: RefObject<HTMLElement>;
    scroller?: Scroller;
    swipeMenuList?: SwipeMenuList;
    activeClass?: string;
    onTap: (target: EventTarget) => void;
    onTouchStart: (evt: React.TouchEvent) => void;
    disabled?: boolean;
}

function getDistance(endPoint: TouchPoint | null, startPoint: TouchPoint | null): number {
    if (!endPoint || !startPoint) {
        return 0;
    }
    return Math.sqrt(Math.pow(endPoint.pageX - startPoint.pageX, 2) + Math.pow(endPoint.pageY - startPoint.pageY, 2));
}

function onTouchMoveShouldCancelTap(endPoint: TouchPoint | null, startPoint: TouchPoint | null): boolean {
    return getDistance(endPoint, startPoint) > TAP_SLOP;
}

function getTouchPoint(evt: TouchEvent): TouchPoint | null {
    return evt.touches.length ? { pageX: evt.touches[0].pageX, pageY: evt.touches[0].pageY } : null;
}

function removeActiveClass(domNode: HTMLElement | null, activeClass?: string): void {
    if (domNode && activeClass) {
        domNode.classList.remove(activeClass);
    }
}

function isScrolling(scroller?: Scroller): boolean {
    return scroller ? scroller.isScrolling : false;
}

function isAnySwipeMenuOpen(swipeMenuList?: SwipeMenuList): boolean {
    return swipeMenuList ? swipeMenuList.openIndex !== -1 : false;
}

let startPoint: TouchPoint | null = null;
let shouldAbortTap: boolean = false;
let captured: HTMLElement | null = null;

export default function createGesture({
    domRef,
    scroller,
    swipeMenuList,
    activeClass,
    onTap,
    onTouchStart,
    disabled
}: GestureProps) {
    const gestureObj = {
        onTouchStart(evt: TouchEvent) {
            const domNode = domRef.current;
            removeActiveClass(domNode, activeClass);
            shouldAbortTap = isScrolling(scroller) || isAnySwipeMenuOpen(swipeMenuList);
            startPoint = getTouchPoint(evt);
            onTouchStart(evt as unknown as React.TouchEvent);

            if (!captured) {
                captured = domNode;
            }

            setTimeout(() => {
                if (!shouldAbortTap && activeClass && captured === domNode && !disabled) {
                    domNode!.classList.add(activeClass);
                }
            }, TAP_DELAY);
        },
        onTouchMove(evt: TouchEvent) {
            const domNode = domRef.current;
            const currentPoint = getTouchPoint(evt);

            if (onTouchMoveShouldCancelTap(currentPoint, startPoint)) {
                shouldAbortTap = true;
                captured = null;
                removeActiveClass(domNode, activeClass);
            }
        },
        onTouchEnd(evt: TouchEvent) {
            const target = <any>evt.target;
            const domNode = domRef.current;

            if (!shouldAbortTap && captured === domNode) {
                setTimeout(() => {
                    if (!disabled) {
                        onTap(target);
                    }
                    removeActiveClass(domNode, activeClass);
                    captured = null;
                }, TAP_DELAY + 10);
            } else if (shouldAbortTap) {
                captured = null;
            }
        },
        onTouchCancel() {
            const domNode = domRef.current;
            removeActiveClass(domNode, activeClass);
        }
    };

    return gestureObj;
}